import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSchoolMealForm } from '../../hooks/useSchoolMealForm';
import InfoSection from '../InfoSection/InfoSection';
import AnswersSection from '../AnswersSection/AnswersSection';
import { getInitials } from '../../utils/helpers';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import './SchoolMealForm.css';

const SchoolMealForm: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { texts, isMealDataLoading } = useAuth();
  const {
    formData,
    updateMealPreference,
    toggleDietaryRestriction,
    addCustomRestriction,
    removeCustomRestriction,
    submit,
    isSubmitting,
    submitError,
    submitSuccess,
    clearError,
    clearSuccess,
  } = useSchoolMealForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submit();
  };

  const parentInitials = getInitials(formData.studentInfo.parentName || '');
  const logo = theme === 'light' 
    ? '/IST logo vector_Logo light.svg' 
    : '/IST logo vector_Logo.svg';

  if (isMealDataLoading) {
    return (
      <div className="school-meal-form-container">
        <div className="school-meal-form-loading" role="status" aria-live="polite">
          <div className="school-meal-form-loading-inner">
            <span className="school-meal-form-loading-spinner" aria-hidden />
            <span className="school-meal-form-loading-text">{t('form.loadingMeals')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="school-meal-form-container">
      <form className="school-meal-form" onSubmit={handleSubmit} aria-label={t('form.title')}>
        <div className="form-header">
          <img src={logo} alt="IST Logo" className="form-logo" />
          <div className="form-title-row">
            <h1 className="form-title">{t('form.title')}</h1>
            {formData.studentInfo.parentName && (
              <div className="parent-user-indicator">
                <div className="parent-user-avatar">
                  <span className="parent-avatar-initials">{parentInitials}</span>
                </div>
                <span className="parent-user-name">{formData.studentInfo.parentName}</span>
              </div>
            )}
          </div>
        </div>

        <InfoSection studentInfo={formData.studentInfo} />

        <AnswersSection
          mealPreferences={formData.mealPreferences}
          dietaryRestrictions={formData.dietaryRestrictions}
          texts={texts}
          onMealUpdate={updateMealPreference}
          onToggleRestriction={toggleDietaryRestriction}
          onAddCustomRestriction={addCustomRestriction}
          onRemoveCustomRestriction={removeCustomRestriction}
        />

        {submitError && (
          <div className="form-error" role="alert">
            <span className="form-error-text">{submitError}</span>
            <button
              type="button"
              className="form-message-close"
              onClick={clearError}
              aria-label="Close error message"
            >
              x
            </button>
          </div>
        )}
        
        {submitSuccess && (
          <div className="form-success" role="alert">
            <span className="form-success-text">{t('form.submitSuccess')}</span>
            <button
              type="button"
              className="form-message-close"
              onClick={clearSuccess}
              aria-label="Close success message"
            >
              x
            </button>
          </div>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="button button-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('form.submitting') : t('form.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SchoolMealForm;

